/* eslint-disable @typescript-eslint/no-explicit-any */
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  derivativeFormSchema,
  DerivativeFormData,
} from "./derivativeForm.schema";
import { useCurrencyList } from "@/hooks/useCurrencyList";

interface DerivativeFormProps {
  derivative?: any; // TODO: Replace with proper DerivativeSecurity type when available
  onSave: (data: DerivativeFormData) => void | Promise<void>;
  onCancel: () => void;
}

export function DerivativeForm({
  derivative,
  onSave,
  onCancel,
}: DerivativeFormProps) {
  const { toast } = useToast();
  const {
    data: availableCurrencies,
    isLoading: isLoadingCurrencies,
    error: errorLoadingCurrencies,
  } = useCurrencyList();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<DerivativeFormData>({
    resolver: zodResolver(derivativeFormSchema),
    defaultValues: {
      derivative_type: derivative?.derivative_type ?? "option",
      underlying_asset: derivative?.underlying_asset ?? "",
      expiration_date: derivative?.expiration_date ?? "",
      exchange: derivative?.exchange ?? "",
      currency: derivative?.currency ?? "USD",
      notes: derivative?.notes ?? "",
      // Option-specific fields
      option_type: derivative?.option_type ?? "call",
      strike_price: derivative?.strike_price ?? undefined,
      premium_paid: derivative?.premium_paid ?? undefined,
      contract_size: derivative?.contract_size ?? undefined,
      // Future-specific fields
      contract_price: derivative?.contract_price ?? undefined,
      margin_requirement: derivative?.margin_requirement ?? undefined,
    },
  });

  const derivativeType = watch("derivative_type");

  const onSubmit = async (data: DerivativeFormData) => {
    try {
      await onSave(data);
      toast({
        title: "Success",
        description: `Derivative ${derivative ? "updated" : "created"} successfully`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to ${derivative ? "update" : "create"} derivative. Please try again.`,
        variant: "destructive",
      });
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4"
      aria-label={
        derivative ? "Edit derivative security" : "Add derivative security"
      }>
      <div className="space-y-2">
        <Label htmlFor="derivative_type">Derivative Type *</Label>
        <Select
          value={derivativeType}
          onValueChange={(value) => {
            setValue("derivative_type", value as "option" | "future");
            // Reset type-specific fields when type changes
            if (value === "option") {
              setValue("option_type", "call");
              setValue("strike_price", undefined);
              setValue("premium_paid", undefined);
              setValue("contract_price", undefined);
              setValue("margin_requirement", undefined);
            } else {
              setValue("option_type", undefined as any);
              setValue("strike_price", undefined);
              setValue("premium_paid", undefined);
              setValue("contract_price", undefined);
              setValue("margin_requirement", undefined);
            }
          }}>
          <SelectTrigger
            id="derivative_type"
            aria-required="true"
            aria-label="Select derivative type"
            autoFocus>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="option">Option</SelectItem>
            <SelectItem value="future">Future</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="underlying_asset">Underlying Asset *</Label>
        <Input
          id="underlying_asset"
          {...register("underlying_asset")}
          placeholder="e.g. AAPL, Gold, Crude Oil"
          aria-required="true"
          aria-describedby={
            errors.underlying_asset ? "underlying-asset-error" : undefined
          }
          aria-invalid={!!errors.underlying_asset}
        />
        {errors.underlying_asset && (
          <p
            id="underlying-asset-error"
            className="text-sm text-destructive"
            role="alert">
            {errors.underlying_asset.message}
          </p>
        )}
      </div>

      {/* Option-specific fields */}
      {derivativeType === "option" && (
        <>
          <div className="space-y-2">
            <Label htmlFor="option_type">Option Type *</Label>
            <Select
              value={watch("option_type")}
              onValueChange={(value) =>
                setValue("option_type", value as "call" | "put")
              }>
              <SelectTrigger
                id="option_type"
                aria-required="true"
                aria-label="Select option type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="call">Call</SelectItem>
                <SelectItem value="put">Put</SelectItem>
              </SelectContent>
            </Select>
            {"option_type" in errors && errors.option_type && (
              <p className="text-sm text-destructive" role="alert">
                {errors.option_type.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="strike_price">Strike Price *</Label>
              <Input
                id="strike_price"
                type="number"
                step="0.01"
                min="0"
                {...register("strike_price", { valueAsNumber: true })}
                placeholder="e.g. 150.00"
                aria-required="true"
                aria-describedby={
                  "strike_price" in errors && errors.strike_price
                    ? "strike-price-error"
                    : undefined
                }
                aria-invalid={"strike_price" in errors && !!errors.strike_price}
              />
              {"strike_price" in errors && errors.strike_price && (
                <p
                  id="strike-price-error"
                  className="text-sm text-destructive"
                  role="alert">
                  {errors.strike_price.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="premium_paid">Premium Paid *</Label>
              <Input
                id="premium_paid"
                type="number"
                step="0.01"
                min="0"
                {...register("premium_paid", { valueAsNumber: true })}
                placeholder="e.g. 5.50"
                aria-required="true"
                aria-describedby={
                  "premium_paid" in errors && errors.premium_paid
                    ? "premium-paid-error"
                    : undefined
                }
                aria-invalid={"premium_paid" in errors && !!errors.premium_paid}
              />
              {"premium_paid" in errors && errors.premium_paid && (
                <p
                  id="premium-paid-error"
                  className="text-sm text-destructive"
                  role="alert">
                  {errors.premium_paid.message}
                </p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Future-specific fields */}
      {derivativeType === "future" && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="contract_price">Contract Price *</Label>
            <Input
              id="contract_price"
              type="number"
              step="0.01"
              min="0"
              {...register("contract_price", { valueAsNumber: true })}
              placeholder="e.g. 75.00"
              aria-required="true"
              aria-describedby={
                "contract_price" in errors && errors.contract_price
                  ? "contract-price-error"
                  : undefined
              }
              aria-invalid={
                "contract_price" in errors && !!errors.contract_price
              }
            />
            {"contract_price" in errors && errors.contract_price && (
              <p
                id="contract-price-error"
                className="text-sm text-destructive"
                role="alert">
                {errors.contract_price.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="margin_requirement">Margin Requirement *</Label>
            <Input
              id="margin_requirement"
              type="number"
              step="0.01"
              min="0"
              {...register("margin_requirement", { valueAsNumber: true })}
              placeholder="e.g. 5000.00"
              aria-required="true"
              aria-describedby={
                "margin_requirement" in errors && errors.margin_requirement
                  ? "margin-req-error"
                  : undefined
              }
              aria-invalid={
                "margin_requirement" in errors && !!errors.margin_requirement
              }
            />
            {"margin_requirement" in errors && errors.margin_requirement && (
              <p
                id="margin-req-error"
                className="text-sm text-destructive"
                role="alert">
                {errors.margin_requirement.message}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="contract_size">Contract Size *</Label>
        <Input
          id="contract_size"
          type="number"
          step="0.000001"
          min="0"
          {...register("contract_size", { valueAsNumber: true })}
          placeholder="e.g. 100"
          aria-required="true"
          aria-describedby={
            errors.contract_size ? "contract-size-error" : "contract-size-hint"
          }
          aria-invalid={!!errors.contract_size}
        />
        {errors.contract_size ? (
          <p
            id="contract-size-error"
            className="text-sm text-destructive"
            role="alert">
            {errors.contract_size.message}
          </p>
        ) : (
          <p id="contract-size-hint" className="text-xs text-muted-foreground">
            Number of units per contract
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="expiration_date">Expiration Date *</Label>
          <Input
            id="expiration_date"
            type="date"
            {...register("expiration_date")}
            aria-required="true"
            aria-describedby={
              errors.expiration_date ? "expiration-date-error" : undefined
            }
            aria-invalid={!!errors.expiration_date}
          />
          {errors.expiration_date && (
            <p
              id="expiration-date-error"
              className="text-sm text-destructive"
              role="alert">
              {errors.expiration_date.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="exchange">Exchange *</Label>
          <Input
            id="exchange"
            {...register("exchange")}
            placeholder="e.g. CBOE, CME"
            aria-required="true"
            aria-describedby={errors.exchange ? "exchange-error" : undefined}
            aria-invalid={!!errors.exchange}
          />
          {errors.exchange && (
            <p
              id="exchange-error"
              className="text-sm text-destructive"
              role="alert">
              {errors.exchange.message}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="currency">Currency *</Label>
        <Select
          value={watch("currency")}
          onValueChange={(value) => setValue("currency", value)}>
          <SelectTrigger
            id="currency"
            aria-required="true"
            aria-label="Select currency">
            <SelectValue />
          </SelectTrigger>
          {isLoadingCurrencies && "Loading Currencies"}
          {!isLoadingCurrencies && !errorLoadingCurrencies ? (
            <SelectContent>
              {availableCurrencies.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          ) : (
            "Failed to load currencies"
          )}
        </Select>
        {errors.currency && (
          <p className="text-sm text-destructive" role="alert">
            {errors.currency.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          {...register("notes")}
          placeholder="Additional notes about this derivative"
          rows={3}
          aria-label="Additional notes"
        />
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          aria-label="Cancel">
          Cancel
        </Button>
        <Button
          type="submit"
          aria-label={derivative ? "Update derivative" : "Create derivative"}>
          {derivative ? "Update" : "Create"}
        </Button>
      </DialogFooter>
    </form>
  );
}
